import { useEffect, useRef, useState } from 'react';
import Video, { ConnectOptions, LocalDataTrack, LocalTrack, Room } from 'twilio-video';

import { Callback, IActiveRoom, IRoom, RoomConfig, RoomPrivacyType } from '../../../types';
import { createRoomName, getKnockStatus, getUpn, isMobile, isRoomOwner } from '../../../utils';
import {
  apiGetActiveMeetRooms,
  apiGetRoomConfig,
  apiJoinRoomAndChat,
  apiPostKnockClear,
  apiPostRoomCreate,
  apiPostRoomDisconnect,
  apiPostRoomKnock,
  apiUpdateRoomPrivacy,
} from '../../../utils/api';
import { useAppState } from '../../../state';
import { MESSAGE_EVENTS } from '../../../constants';

// @ts-ignore
window.TwilioVideo = Video;

export default function useRoom(localTracks: LocalTrack[], onError: Callback, options?: ConnectOptions) {
  const { domain, isFetching, isUsernameInUrl, password, getToken, roomOwnerUsername, roomOwnerDomain } = useAppState();
  const [room, setRoom] = useState<Room | null>(null);
  const [apiRoom, setApiRoom] = useState<IRoom | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isKnocking, setIsKnocking] = useState(false);
  const [knockedRoomUpn, setKnockedRoomUpn] = useState('');
  const [showRequestRejectedSnackbar, setShowRequestRejectedSnackbar] = useState(false);
  const [isWaitingForHostToCreateRoom, setIsWaitingForHostToCreateRoom] = useState(false);
  const optionsRef = useRef(options);
  const roomCreatedIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const knockStatusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [userBooted, setUserBooted] = useState(false);
  const dataTrack = useRef(new LocalDataTrack());
  const [isJoining, setIsJoining] = useState(false);
  const [rcRoomId, setRcRoomId] = useState('');
  const [rcUserId, setRcUserId] = useState('');
  const [rcAuthToken, setRcAuthToken] = useState('');

  useEffect(() => {
    if (userBooted) {
      setTimeout(() => {
        setUserBooted(false);
      }, 4000);
    }
  }, [userBooted]);

  /**
   * Cleans up the intervals when the component unmounts
   */
  const cleanUpIntervals = () => {
    roomCreatedIntervalRef.current && clearInterval(roomCreatedIntervalRef.current);
    roomCreatedIntervalRef.current = null;
    knockStatusIntervalRef.current && clearInterval(knockStatusIntervalRef.current);
    knockStatusIntervalRef.current = null;
  };

  useEffect(() => {
    return () => cleanUpIntervals();
  }, []);

  useEffect(() => {
    // This allows the connect function to always access the most recent version of the options object. This allows us to
    // reliably use the connect function at any time.
    optionsRef.current = options;
  }, [options]);

  /**
   * Connects to a room using a token
   * @param token string - Twilio token
   */
  const connect = (token: string, roomName: string, useDataTrack: boolean = false) => {
    setIsConnecting(true);
    const tracks = useDataTrack ? [...localTracks, dataTrack.current] : localTracks;
    return Video.connect(token, {
      ...optionsRef.current,
      name: roomName,
      tracks,
      dominantSpeaker: true,
    }).then(
      newRoom => {
        setRoom(newRoom);
        // VideoRoomMonitor.registerVideoRoom(newRoom);
        const disconnect = () => {
          if (isRoomOwner(newRoom)) {
            // Use sendBeacon for reliable delivery during page unload
            apiPostRoomDisconnect(newRoom.sid);
          }
          newRoom.disconnect();
        };

        // This app can add up to 16 'participantDisconnected' listeners to the room object, which can trigger
        // a warning from the EventEmitter object. Here we increase the max listeners to suppress the warning.
        newRoom.setMaxListeners(16);

        newRoom.once('disconnected', () => {
          // Reset the room only after all other `disconnected` listeners have been called.
          setTimeout(() => setRoom(null));
          window.removeEventListener('beforeunload', disconnect);
          window.removeEventListener('unload', disconnect);

          if (isMobile) {
            window.removeEventListener('pagehide', disconnect);
          }
        });

        // @ts-ignore
        window.twilioRoom = newRoom;

        newRoom.localParticipant.videoTracks.forEach(publication =>
          // All video tracks are published with 'low' priority because the video track
          // that is displayed in the 'MainParticipant' component will have it's priority
          // set to 'high' via track.setPriority()
          publication.setPriority('low')
        );

        newRoom.participants.forEach(participant => {
          participant.tracks.forEach(publication => {
            publication.on('subscribed', track => {
              if (track.kind === 'data') {
                track.on('message', data => {
                  const { type, participant } = JSON.parse(data as string);
                  if (newRoom.localParticipant.sid === participant.sid) {
                    if (type === MESSAGE_EVENTS.REMOVE_AUDIO) {
                      newRoom.localParticipant.audioTracks.forEach(audioTrack => {
                        audioTrack.track?.disable();
                      });
                    } else if (type === MESSAGE_EVENTS.KICK) {
                      newRoom.disconnect();
                    }
                  }
                });
              }
            });
          });
        });

        setIsConnecting(false);

        // Add listeners to disconnect from the room when a user closes their browser
        window.addEventListener('beforeunload', disconnect);
        window.addEventListener('unload', disconnect);

        if (isMobile) {
          // Add a listener to disconnect from the room when a mobile user closes their browser
          window.addEventListener('pagehide', disconnect);
        }

        // Also handle visibility change (when user switches tabs)
        // document.addEventListener('visibilitychange', () => {
        //   if (document.visibilityState === 'hidden') {
        //     // Small delay to allow other cleanup to happen first
        //     setTimeout(disconnect, 100);
        //   }
        // });
      },
      error => {
        onError(error);
        setIsConnecting(false);
      }
    );
  };

  /**
   * Creates a room using REST endpoint and connects to it by calling the connect function
   * @param token string - Twilio token
   * @param roomName string - Name of the room
   * @param shouldRecord boolean  - Whether the room should be recorded or not
   */
  const createRoomAndConnect = async (
    token: string,
    roomName: string,
    shouldRecord: boolean = false,
    username: string
  ) => {
    let res;
    try {
      res = await apiPostRoomCreate(roomName, shouldRecord, 'group', username);
      await connect(token, roomName, true);
      setApiRoom(res.room);
      setRcRoomId(res.roomId);
      setRcUserId(res.userId);
      setRcAuthToken(res.rcAuthToken);
    } catch (error) {
      if (res?.room) {
        apiPostRoomDisconnect(res.room.sid);
      }
      console.error('Error creating the room.');
      onError(error);
    }
  };

  /**
   * Joins a private room, sends a knock request to the room owner and waits for the owner to accept/reject the request
   * @param hostUpn string - UPN of the room owner
   * @param roomName string - Name of the room
   * @param username string - Username of the user sending the request
   */
  const joinPrivateRoom = async (hostUpn: string, roomName: string, username: string) => {
    // knock the room and wait for the host to accept/reject the request
    setIsKnocking(true);
    setKnockedRoomUpn(hostUpn);
    try {
      await apiPostRoomKnock(username, hostUpn);
      // check the status of the knock request every 5 seconds until it is accepted/rejected
      knockStatusIntervalRef.current = setInterval(async () => {
        try {
          const knockStatus = await getKnockStatus(username, hostUpn);
          if (knockStatus !== -1) {
            if (knockStatus === 0) {
              // join request accepted
              try {
                await joinRoom({ username, roomName });
              } catch (error) {
                console.error('Error joining room after knock accepted:', error);
                onError(error);
              }
            } else {
              // join request rejected
              setShowRequestRejectedSnackbar(true);
              setTimeout(() => {
                setShowRequestRejectedSnackbar(false);
              }, 3000);
            }
            setIsKnocking(false);
            // clear the interval
            knockStatusIntervalRef.current && clearInterval(knockStatusIntervalRef.current);
            knockStatusIntervalRef.current = null;
            await apiPostKnockClear(username, hostUpn, knockStatus.toString());
            setKnockedRoomUpn('');
          }
        } catch (error) {
          console.error('Error checking knock status:', error);
          // Don't call onError here as it's in an interval callback
        }
      }, 5000);
    } catch (e) {
      console.log(e);
      setIsKnocking(false);
      setKnockedRoomUpn('');
    }
  };

  /**
   * Checks whether the room is created before joining someone else's room
   * Waits for the host to create the room
   * Once the host creates the room, this promise resolves
   * @param roomName string - Name of the room
   */
  const isRoomCreated = async (roomName: string) => {
    let promise = new Promise<RoomConfig | null>((resolve, reject) => {
      async function recurring() {
        // grabs config data of room
        try {
          const fetchedRoom = await apiGetRoomConfig(roomName);
          if (fetchedRoom.RoomMembers && fetchedRoom.RoomMembers.length !== 0) {
            // room created, resolve promise with room and clear interval
            roomCreatedIntervalRef.current && clearInterval(roomCreatedIntervalRef.current);
            roomCreatedIntervalRef.current = null;
            resolve(fetchedRoom);
          } else {
            if (!isWaitingForHostToCreateRoom) {
              // display waiting modal
              setIsWaitingForHostToCreateRoom(true);
            }
          }
        } catch (err) {
          console.error('Error fetching room config', err);
        }
      }
      roomCreatedIntervalRef.current = setInterval(() => {
        recurring();
      }, 4000);
      recurring();
    });
    return promise;
  };

  /**
   * Gets the token and connects to the room
   * Creates the room if it doesn't exist (in case of the host)
   * @param roomName string - Name of the room
   * @param username string - Username of the user
   * @param privacy RoomPrivacyType - Privacy of the room
   * @param createRoom boolean - Whether the room should be created using REST enpoint
   * @param shouldRecord boolean - Whether the room should be recorded or not
   */
  const joinRoom = async ({
    roomName,
    username,
    privacy = 'public',
    createRoom = false,
    shouldRecord = false,
  }: {
    roomName: string;
    username: string;
    createRoom?: boolean;
    privacy?: RoomPrivacyType;
    shouldRecord?: boolean;
  }) => {
    try {
      // grabs auth token
      const token = await getToken(username, roomName);
      if (createRoom) {
        await createRoomAndConnect(token, roomName, shouldRecord, username);
      } else {
        const res = await apiJoinRoomAndChat(username, roomName);
        await connect(token, roomName);
        setApiRoom(res.room);
        setRcRoomId(res.rcRoomId);
        setRcUserId(res.rcUserId);
        setRcAuthToken(res.rcAuthToken);
      }
      if (password) {
        // updates room privacy
        await apiUpdateRoomPrivacy(getUpn(username, domain), privacy === 'private' ? 1 : 0);
      }
    } catch (error) {
      console.error('Error joining room:', error);
      onError(error);
      throw error; // Re-throw to allow handleJoinRoom to handle it
    }
  };

  /**
   * Joins the room of another user, checks if the room is private or public and joins accordingly
   * @param room RoomConfig
   * @param roomName string
   * @param username string
   */
  const joinOtherUserRoom = async (roomConfig: RoomConfig, roomName: string, username: string) => {
    if (isFetching || isConnecting) return;

    try {
      if (roomConfig.RoomPrivacy === 1 && isUsernameInUrl) {
        // room is private, join private room
        const activeRooms = await apiGetActiveMeetRooms(username, domain);
        const ownerRoom = activeRooms.find((_room: IActiveRoom) => _room.id === roomConfig.ID);
        if (ownerRoom) {
          const upn = getUpn(ownerRoom.username, domain);
          joinPrivateRoom(upn, roomName, username);
        }
      } else if (roomConfig.RoomPrivacy === 1 && !isUsernameInUrl) {
        // when the user is someone new i.e. has entered the username manually
        // extract the owner username from roomname using domain
        const ownerUsername = roomName.substring(0, roomName.match(new RegExp(domain))?.index);
        const upn = getUpn(ownerUsername, domain);
        joinPrivateRoom(upn, roomName, username);
      } else {
        // room is public => join room
        await joinRoom({ username, roomName });
      }
    } catch (error) {
      console.error('Error joining other user room', error);
      onError(error);
      throw error; // Re-throw to allow handleJoinRoom to handle it
    }
  };

  /**
   * Handles the join button click, performs various checks and joins the room
   * @param privacy RoomPrivacyType - Privacy of the room
   * @param roomName string - Name of the room
   * @param shouldRecord boolean - Whether the room should be recorded or not
   * @param username string - Username of the user
   */
  const handleJoinRoom = async ({
    privacy,
    roomName,
    shouldRecord,
    username,
    skipRoomCheck = false,
  }: {
    privacy: RoomPrivacyType;
    roomName: string;
    shouldRecord: boolean;
    username: string;
    skipRoomCheck?: boolean;
  }) => {
    setIsJoining(true);
    try {
      if (roomOwnerUsername) {
        const roomConfig = await apiGetRoomConfig(roomName);

        // Wait for the host to create the room before joining (same check
        // as the non-roomOwnerUsername path). Without this, the client
        // would call joinRoomAndChat before the Twilio room exists → 404.
        if (!roomConfig.RoomMembers || roomConfig.RoomMembers.length === 0) {
          const fetchedRoom = await isRoomCreated(roomName);
          if (fetchedRoom) {
            await joinOtherUserRoom(fetchedRoom, roomName, username);
            setIsWaitingForHostToCreateRoom(false);
          }
        } else if (roomConfig.RoomPrivacy === 1) {
          let fromUpn = getUpn(roomOwnerUsername, roomOwnerDomain);
          await joinPrivateRoom(fromUpn, roomName, username);
        } else {
          // The room is public and host has already joined.
          console.log('joining room(public)');
          await joinRoom({ roomName, username });
        }
      } else {
        if (!skipRoomCheck && roomName.toLowerCase() !== createRoomName(username, domain).toLowerCase()) {
          // Joining someone else's room
          // is room created? This promise is resolved only when the room is created
          const fetchedRoom = await isRoomCreated(roomName);
          if (fetchedRoom) {
            // room created
            await joinOtherUserRoom(fetchedRoom, roomName, username);
            setIsWaitingForHostToCreateRoom(false);
          }
        } else {
          // Creating own room
          await joinRoom({ username, roomName, privacy, shouldRecord, createRoom: true });
        }
      }
    } catch (error) {
      console.error('Error in handleJoinRoom:', error);
      onError(error);
      // Don't re-throw - we want to reset the UI state even on error
    } finally {
      setIsJoining(false);
    }
  };

  return {
    apiRoom,
    knockedRoomUpn,
    isConnecting,
    isKnocking,
    isJoining,
    isWaitingForHostToCreateRoom,
    room,
    rcAuthToken,
    rcRoomId,
    rcUserId,
    showRequestRejectedSnackbar,
    userBooted,
    dataTrack: dataTrack.current,
    handleJoinRoom,
    joinOtherUserRoom,
    setShowRequestRejectedSnackbar,
    setUserBooted,
  };
}
